import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Box, MenuItem } from "@mui/material";
import { C, fmtINR } from "../constants";
import { IPD_COLORS, ipdSemanticSx, ipdCardSx } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import PatientSearchBar from "./PatientSearchBar";
import {
  listAllocatedAdmissions,
  admissionToPatientContext,
} from "../billing-api";
import {
  postAdmissionBillingSetup,
  postAdmissionCharge,
  calculateAdmissionPayout,
  getAdmissionPayoutLinesMapped,
  searchChargeMaster,
  parseChargePostResult,
} from "../payout-api";

const CHARGE_TYPES = ["TEST", "RADIOLOGY", "MEDICATION"];
const SEARCH_DEBOUNCE_MS = 400;

function collectDoctorProvider(entry, role, admissionId) {
  if (!entry?.billing_provider_id) return null;
  const name = entry.name || "Doctor";
  const specialty = entry.specialty || "";
  const roleLabel = role.replace(/_/g, " ");
  return {
    id: String(entry.billing_provider_id),
    name,
    specialty,
    role: roleLabel,
    admissionId,
    label: `${name} · ${roleLabel}${specialty ? ` · ${specialty}` : ""}`,
  };
}

function buildBillingProviderOptions(admissions, selectedAdmission, payoutDoctors = []) {
  const map = new Map();
  const add = (opt) => {
    if (!opt?.id) return;
    if (!map.has(opt.id)) map.set(opt.id, opt);
  };

  if (selectedAdmission?.doctors?.admitting) {
    add(collectDoctorProvider(selectedAdmission.doctors.admitting, "Admitting", selectedAdmission.admission_id));
  }
  (selectedAdmission?.doctors?.attending || []).forEach((d) => {
    add(collectDoctorProvider(d, d.role || "Attending", selectedAdmission.admission_id));
  });

  admissions.forEach((adm) => {
    if (adm.doctors?.admitting) {
      add(collectDoctorProvider(adm.doctors.admitting, "Admitting", adm.admission_id));
    }
    (adm.doctors?.attending || []).forEach((d) => {
      add(collectDoctorProvider(d, d.role || "Attending", adm.admission_id));
    });
  });

  payoutDoctors.forEach((d) => {
    if (!d?.id) return;
    add({
      id: String(d.id),
      name: d.name || "Doctor",
      specialty: d.specialty || "",
      role: "Payout register",
      label: `${d.name || "Doctor"}${d.specialty ? ` · ${d.specialty}` : ""} · Payout register`,
    });
  });

  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

const LINE_STATUS_TONE = {
  CALCULATED: "info",
  HOLD: "warning",
  APPROVED: "warning",
  PAID: "success",
  REVERSAL: "danger",
};

// Translates ipdSemanticSx's MUI-sx shape ({bgcolor, color, border}) into this
// file's local inline-style shape ({bg, color, border}). `border` is sourced
// straight from IPD_COLORS (not parsed out of ipdSemanticSx's already-assembled
// "1px solid <color>" string) since the JSX below re-wraps it as `1px solid ${s.border}`.
function semanticTone(variant) {
  const sx = ipdSemanticSx(variant);
  return { bg: sx.bgcolor, color: sx.color, border: IPD_COLORS[variant]?.border };
}

function Badge({ children, tone = "info" }) {
  const tones = {
    info: semanticTone("info"),
    success: semanticTone("success"),
    warning: semanticTone("warning"),
    muted: { bg: IPD_COLORS.pageBg, color: IPD_COLORS.textSecondary, border: IPD_COLORS.border },
    danger: semanticTone("error"),
  };
  const s = tones[tone] || tones.info;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 20,
      fontSize: 10, fontWeight: 700, background: s.bg, color: s.color,
      border: `1px solid ${s.border}`, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function StepCard({ step, title, desc, children, done }) {
  return (
    <Box sx={{
      border: `1px solid ${done ? IPD_COLORS.success.border : C.border}`, borderRadius: "10px",
      bgcolor: done ? IPD_COLORS.success.bg : IPD_COLORS.cardBg, overflow: "hidden",
    }}>
      <div style={{
        padding: "12px 16px", borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 10,
        background: done ? IPD_COLORS.success.bg : IPD_COLORS.pageBg,
      }}>
        <div style={{
          width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 800, background: done ? C.success : C.primary, color: IPD_COLORS.white,
        }}>{done ? "✓" : step}</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</div>
          <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>{desc}</div>
        </div>
      </div>
      <div style={{ padding: "16px" }}>{children}</div>
    </Box>
  );
}

export default function AdmissionPayoutWorkflow({ onPayoutUpdated, payoutDoctors = [] }) {
  const [expanded, setExpanded] = useState(false);
  const [admissions, setAdmissions] = useState([]);
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchError, setSearchError] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedAdmission, setSelectedAdmission] = useState(null);

  const [setupDone, setSetupDone] = useState(false);
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupMsg, setSetupMsg] = useState("");

  const [chargeType, setChargeType] = useState("TEST");
  const [serviceQuery, setServiceQuery] = useState("");
  const [chargeOptions, setChargeOptions] = useState([]);
  const [chargeSearchBusy, setChargeSearchBusy] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [quantity, setQuantity] = useState("1");
  const [payoutMode, setPayoutMode] = useState("admitting");
  const [referrerId, setReferrerId] = useState("");
  const [autoCalculate, setAutoCalculate] = useState(true);
  const [chargeRemarks, setChargeRemarks] = useState("");
  const [chargeBusy, setChargeBusy] = useState(false);
  const [chargeMsg, setChargeMsg] = useState("");
  const [lastCharge, setLastCharge] = useState(null);

  const [ledgerId, setLedgerId] = useState("");
  const [chargeEventId, setChargeEventId] = useState("");
  const [forceRecalculate, setForceRecalculate] = useState(false);
  const [calcBusy, setCalcBusy] = useState(false);
  const [calcMsg, setCalcMsg] = useState("");

  const [lines, setLines] = useState([]);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");

  const searchTimer = useRef(null);

  const admissionId = selectedPatient?.admission_id || selectedPatient?.admissionId;

  const billingProviderOptions = useMemo(
    () => buildBillingProviderOptions(admissions, selectedAdmission, payoutDoctors),
    [admissions, selectedAdmission, payoutDoctors],
  );

  const admittingProviderId = selectedAdmission?.doctors?.admitting?.billing_provider_id || "";

  useEffect(() => {
    if (payoutMode !== "referral" || billingProviderOptions.length === 0) return;
    if (referrerId && billingProviderOptions.some((o) => o.id === referrerId)) return;
    const preferred =
      billingProviderOptions.find((o) => o.role !== "Admitting" && o.admissionId === selectedAdmission?.admission_id) ||
      billingProviderOptions.find((o) => o.role !== "Admitting") ||
      billingProviderOptions[0];
    setReferrerId(preferred?.id || "");
  }, [payoutMode, billingProviderOptions, selectedAdmission?.admission_id]);

  const loadAdmissions = useCallback(async () => {
    setLoadingAdmissions(true);
    setSearchError("");
    try {
      setAdmissions(await listAllocatedAdmissions("Allocated"));
    } catch (e) {
      setSearchError(e.message || "Failed to load admissions");
      setAdmissions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, []);

  useEffect(() => { loadAdmissions(); }, [loadAdmissions]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = serviceQuery.trim();
    if (!q || selectedService) {
      setChargeOptions([]);
      return undefined;
    }
    searchTimer.current = setTimeout(async () => {
      setChargeSearchBusy(true);
      try {
        setChargeOptions(await searchChargeMaster({ name: q, type: chargeType, limit: 20 }));
      } catch {
        setChargeOptions([]);
      } finally {
        setChargeSearchBusy(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchTimer.current);
  }, [serviceQuery, chargeType, selectedService]);

  const resetWorkflow = () => {
    setSetupDone(false);
    setSetupMsg("");
    setSelectedService(null);
    setServiceQuery("");
    setChargeOptions([]);
    setChargeMsg("");
    setLastCharge(null);
    setLedgerId("");
    setChargeEventId("");
    setCalcMsg("");
    setLines([]);
    setVerifyMsg("");
    setReferrerId("");
  };

  const handleSelectAdmission = (row) => {
    const raw = admissions.find((a) => a.admission_id === row.admission_id) || row;
    setSelectedAdmission(raw);
    setSelectedPatient(admissionToPatientContext(raw));
    resetWorkflow();
  };

  const handleClearAdmission = () => {
    setSelectedPatient(null);
    setSelectedAdmission(null);
    setReferrerId("");
    resetWorkflow();
  };

  const handleBillingSetup = async () => {
    if (!admissionId) return;
    setSetupBusy(true);
    setSetupMsg("");
    try {
      const res = await postAdmissionBillingSetup(admissionId);
      setSetupDone(true);
      setSetupMsg(res.message || "Billing setup completed.");
    } catch (e) {
      setSetupMsg(e.message || "Billing setup failed");
    } finally {
      setSetupBusy(false);
    }
  };

  const handlePostCharge = async () => {
    if (!admissionId || !selectedService?.service_code) return;
    setChargeBusy(true);
    setChargeMsg("");
    try {
      const payload = {
        service_code: selectedService.service_code,
        quantity: Math.max(1, Number(quantity) || 1),
        calculate_doctor_payout: autoCalculate,
        source_module: "IPD",
        remarks: chargeRemarks.trim() || selectedService.name || selectedService.service_name || "Doctor payout charge",
      };
      if (payoutMode === "referral") {
        if (!referrerId) throw new Error("Select a referring doctor from the list.");
        payload.referred_by_provider_id = referrerId;
        payload.payout_eligible = true;
      }
      const res = await postAdmissionCharge(admissionId, payload);
      const parsed = parseChargePostResult(res);
      setLastCharge(parsed);
      setLedgerId(parsed.billingLedgerId);
      setChargeEventId(parsed.chargeEventId);
      setChargeMsg(
        parsed.autoPayoutLines?.length
          ? `Charge posted · ${parsed.autoPayoutLines.length} payout line(s) auto-calculated.`
          : parsed.billingLedgerId
            ? `Charge posted · ledger ${parsed.billingLedgerId.slice(0, 8)}…`
            : "Charge posted successfully.",
      );
      if (parsed.autoPayoutLines?.length) {
        await handleVerifyLines();
        onPayoutUpdated?.();
      }
    } catch (e) {
      setChargeMsg(e.message || "Failed to post charge");
    } finally {
      setChargeBusy(false);
    }
  };

  const handleCalculate = async () => {
    if (!admissionId) return;
    setCalcBusy(true);
    setCalcMsg("");
    try {
      const body = { force_recalculate: forceRecalculate, remarks: "Manual payout calc from Doctor Payouts UI" };
      if (ledgerId.trim()) body.billing_ledger_id = ledgerId.trim();
      if (chargeEventId.trim()) body.charge_event_id = chargeEventId.trim();
      await calculateAdmissionPayout(admissionId, body);
      setCalcMsg("Payout calculated.");
      await handleVerifyLines();
      onPayoutUpdated?.();
    } catch (e) {
      setCalcMsg(e.message || "Calculate failed");
    } finally {
      setCalcBusy(false);
    }
  };

  const handleVerifyLines = async () => {
    if (!admissionId) return;
    setVerifyBusy(true);
    setVerifyMsg("");
    try {
      const list = await getAdmissionPayoutLinesMapped(admissionId);
      setLines(list);
      setVerifyMsg(list.length ? `${list.length} payout line(s) on this admission.` : "No payout lines yet.");
    } catch (e) {
      setVerifyMsg(e.message || "Failed to load payout lines");
      setLines([]);
    } finally {
      setVerifyBusy(false);
    }
  };

  return (
    <Box sx={{ ...ipdCardSx, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%", padding: "14px 18px", border: "none", background: IPD_COLORS.pageBg,
          display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
          borderBottom: expanded ? `1px solid ${C.border}` : "none",
        }}
      >
        <div style={{ textAlign: "left" }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Admission payout workflow</div>
          <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>
            Billing setup → post charge → calculate → verify lines (per admission)
          </div>
        </div>
        <SvgIcon name={expanded ? "chevron-up" : "chevron-down"} size={18} color={C.textSub} />
      </button>

      {expanded && (
        <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: 16 }}>
          <PatientSearchBar
            admissions={admissions}
            loadingAdmissions={loadingAdmissions}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSelect={handleSelectAdmission}
            selectedPatient={selectedPatient}
            onClear={handleClearAdmission}
            onRefresh={loadAdmissions}
            searchError={searchError}
            selectHint="select admission to run payout steps"
          />

          {selectedAdmission && (
            <div style={{
              padding: "12px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
              background: IPD_COLORS.pageBg, fontSize: 12, color: C.textSub, lineHeight: 1.6,
            }}>
              <strong style={{ color: C.text }}>Admitting doctor:</strong> {selectedAdmission.doctors?.admitting?.name || selectedAdmission.doctor_name || "—"}
              {admittingProviderId && (
                <span> · billing provider <code style={{ fontSize: 11 }}>{admittingProviderId.slice(0, 8)}…</code></span>
              )}
            </div>
          )}

          {selectedPatient && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
              <StepCard step={1} title="Billing setup" desc="POST …/billing_setup/ — once per admission" done={setupDone}>
                <IpdButton type="button" onClick={handleBillingSetup} disabled={setupBusy || !admissionId}>
                  {setupBusy ? "Running…" : setupDone ? "Re-run setup" : "Run billing setup"}
                </IpdButton>
                {setupMsg && <p style={{ margin: "10px 0 0", fontSize: 12, color: setupDone ? C.success : C.danger }}>{setupMsg}</p>}
              </StepCard>

              <StepCard step={2} title="Post charge" desc="POST …/billing/charges/ — creates ledger + optional auto-calculate" done={Boolean(lastCharge)}>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <IpdFormField label="Charge type">
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {CHARGE_TYPES.map((t) => (
                        <IpdButton
                          key={t}
                          type="button"
                          size="small"
                          variant={chargeType === t ? "contained" : "outlined"}
                          onClick={() => { setChargeType(t); setSelectedService(null); setServiceQuery(""); }}
                        >
                          {t}
                        </IpdButton>
                      ))}
                    </div>
                  </IpdFormField>
                  <div style={{ position: "relative" }}>
                    <IpdFormField label="Service (search_charge_master)">
                      <IpdTextField
                        fullWidth
                        value={selectedService ? (selectedService.name || selectedService.service_name || selectedService.service_code) : serviceQuery}
                        onChange={(e) => { setSelectedService(null); setServiceQuery(e.target.value); }}
                        placeholder="Search service name…"
                      />
                    </IpdFormField>
                    {!selectedService && serviceQuery.trim() && (
                      <div style={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
                        background: IPD_COLORS.cardBg, border: `1px solid ${C.border}`, borderRadius: 8,
                        maxHeight: 200, overflow: "auto", marginTop: 4, boxShadow: "0 8px 24px rgba(13,35,63,0.12)",
                      }}>
                        {chargeSearchBusy ? (
                          <div style={{ padding: 12, fontSize: 12, color: C.textSub }}>Searching…</div>
                        ) : chargeOptions.length === 0 ? (
                          <div style={{ padding: 12, fontSize: 12, color: C.textSub }}>No services found.</div>
                        ) : chargeOptions.map((opt) => (
                          <button key={opt.service_code || opt.id} type="button"
                            onClick={() => { setSelectedService(opt); setServiceQuery(""); setChargeOptions([]); }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 12px", border: "none", borderBottom: `1px solid ${C.border}`, background: IPD_COLORS.cardBg, cursor: "pointer", fontSize: 12 }}
                          >
                            <div style={{ fontWeight: 600 }}>{opt.name || opt.service_name}</div>
                            <div style={{ color: C.textSub, fontSize: 11 }}>{opt.service_code} · {opt.type || chargeType}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <IpdFormField label="Quantity">
                      <IpdTextField fullWidth type="number" inputProps={{ min: 1 }} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                    </IpdFormField>
                    <IpdFormField label="Payout mode">
                      <IpdTextField fullWidth select value={payoutMode} onChange={(e) => setPayoutMode(e.target.value)}>
                        <MenuItem value="admitting">Admitting doctor</MenuItem>
                        <MenuItem value="referral">Referral commission</MenuItem>
                      </IpdTextField>
                    </IpdFormField>
                  </div>
                  {payoutMode === "referral" && (
                    <IpdFormField label="Referring doctor (billing provider)">
                      {billingProviderOptions.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 12, color: C.textSub }}>
                          No billing providers found. Run billing setup or refresh admissions.
                        </p>
                      ) : (
                        <IpdTextField fullWidth select value={referrerId} onChange={(e) => setReferrerId(e.target.value)}>
                          <MenuItem value="">Select referring doctor…</MenuItem>
                          {billingProviderOptions.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                              {r.label} · {r.id.slice(0, 8)}…
                            </MenuItem>
                          ))}
                        </IpdTextField>
                      )}
                    </IpdFormField>
                  )}
                  <IpdFormField label="Remarks">
                    <IpdTextField fullWidth value={chargeRemarks} onChange={(e) => setChargeRemarks(e.target.value)} />
                  </IpdFormField>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={autoCalculate} onChange={(e) => setAutoCalculate(e.target.checked)} />
                    Auto-calculate doctor payout on charge
                  </label>
                  <IpdButton type="button" onClick={handlePostCharge} disabled={chargeBusy || !selectedService}>
                    {chargeBusy ? "Posting…" : "Post charge"}
                  </IpdButton>
                  {chargeMsg && <p style={{ margin: 0, fontSize: 12, color: lastCharge ? C.success : C.danger }}>{chargeMsg}</p>}
                </div>
              </StepCard>

              <StepCard step={3} title="Calculate payout" desc="POST …/doctor-payouts/calculate/ — if not auto on charge" done={Boolean(calcMsg && !calcMsg.includes("fail"))}>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <IpdFormField label="Billing ledger ID">
                    <IpdTextField
                      fullWidth
                      value={ledgerId}
                      onChange={(e) => setLedgerId(e.target.value)}
                      placeholder="From charge response"
                      sx={{ "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 11 } }}
                    />
                  </IpdFormField>
                  <IpdFormField label="Charge event ID (optional)">
                    <IpdTextField
                      fullWidth
                      value={chargeEventId}
                      onChange={(e) => setChargeEventId(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 11 } }}
                    />
                  </IpdFormField>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={forceRecalculate} onChange={(e) => setForceRecalculate(e.target.checked)} />
                    Force recalculate
                  </label>
                  <IpdButton type="button" onClick={handleCalculate} disabled={calcBusy}>
                    {calcBusy ? "Calculating…" : "Calculate payout"}
                  </IpdButton>
                  {calcMsg && <p style={{ margin: 0, fontSize: 12, color: calcMsg.includes("fail") ? C.danger : C.success }}>{calcMsg}</p>}
                </div>
              </StepCard>

              <StepCard step={4} title="Verify payout lines" desc="GET …/doctor-payouts/ — confirm CALCULATED lines" done={lines.length > 0}>
                <IpdButton type="button" variant="outlined" onClick={handleVerifyLines} disabled={verifyBusy} sx={{ mb: 1.5 }}>
                  {verifyBusy ? "Loading…" : "Refresh payout lines"}
                </IpdButton>
                {verifyMsg && <p style={{ margin: "0 0 12px", fontSize: 12, color: C.textSub }}>{verifyMsg}</p>}
                {lines.length > 0 && (
                  <div style={{ overflow: "auto", maxHeight: 280, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: IPD_COLORS.pageBg, color: C.textSub, fontSize: 10, textTransform: "uppercase" }}>
                          {["Date", "Service", "Rule", "Net", "Status", "Role"].map((h) => (
                            <th key={h} style={{ padding: "8px 10px", textAlign: h === "Net" ? "right" : "left", fontWeight: 700 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((ln) => (
                          <tr key={ln.id} style={{ borderTop: `1px solid ${C.border}` }}>
                            <td style={{ padding: "8px 10px", color: C.textSub }}>{ln.date}</td>
                            <td style={{ padding: "8px 10px" }}>
                              <div style={{ fontWeight: 600 }}>{ln.service}</div>
                              <div style={{ color: C.textSub }}>{ln.patient} · {ln.code}</div>
                            </td>
                            <td style={{ padding: "8px 10px", color: C.textSub }}>{ln.rule?.name || "—"}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{fmtINR(ln.net)}</td>
                            <td style={{ padding: "8px 10px" }}><Badge tone={LINE_STATUS_TONE[ln.status] || "muted"}>{ln.status}</Badge></td>
                            <td style={{ padding: "8px 10px", fontSize: 10, color: C.textSub }}>{ln.payoutRole?.replace(/_/g, " ") || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </StepCard>
            </div>
          )}
        </div>
      )}
    </Box>
  );
}
